/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card, Col, Flex, Radio, RadioChangeEvent, Row } from "antd";
import { useEffect, useState } from "react";
import { FieldErrors } from "react-hook-form";
import {
  DateField,
  InputField,
  SelectField,
} from "../../../../../atoms/FormFields";
import { CheckboxGroupProps } from "antd/es/checkbox";
import ProductionTeams from "./ProductionTeams";
import { ProductionType } from "../../../../PotentialDeals/ducks/types";

// Define or import the FormValues type
interface FormValues {
  name: string;
  description?: string;
  frequency?: string;
  quantity_unit?: string;
  take_off?: string;
  production_rate?: number;
  subcontractors?: string[];
  start_date: string;
  end_date: string;
  manager_id: string;
}

interface PropsType {
  control: any;
  errors: FieldErrors<FormValues>;
  append: any;
  remove: any;
  fields: any;
  setValue: any;
  renewOption: string;
  pricingOption: string;
  setPricingOption: (option: string) => void;
  setRenewOption: (option: string) => void;
  setLoading: (loading: boolean) => void;
  productionFormData: ProductionType | null;
  contractDetailDataById: any | null;
}

export default function AssigneesTerms(props: PropsType) {
  const {
    control,
    errors,
    append,
    remove,
    fields,
    setValue,
    productionFormData,
    renewOption,
    setRenewOption,
    pricingOption,
    setPricingOption,
    contractDetailDataById,
  } = props;

  const [crewListDropdown, setCrewListDropdown] = useState<
    { label: string; value: string }[]
  >([]);
  const [managerListDropdown, setManagerListDropdown] = useState<
    { label: string; value: string }[]
  >([]);
  const [productionListDropdown, setProductionListDropdown] = useState<
    { label: string; value: string }[]
  >([]);

  const onRenewChange = (e: RadioChangeEvent) => {
    setRenewOption(e.target.value);
  };
  const onPricingChange = (e: RadioChangeEvent) => {
    setPricingOption(e.target.value);
  };

  const optionsPricing: CheckboxGroupProps<string>["options"] = [
    { label: "Fixed price", value: "fixed_price" },
    { label: "Time & material", value: "time_material" },
  ];

  const optionsRenewable: CheckboxGroupProps<string>["options"] = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" },
  ];

  const netTermDropdown = [
    { value: "due_upon_receipt", label: "Due upon receipt" },
    { value: "net_15", label: "Net 15" },
    { value: "net_30", label: "Net 30" },
    { value: "net_45", label: "Net 45" },
    { value: "net_60", label: "Net 60" },
    { value: "net_90", label: "Net 90" },
  ];

  const billingDropdown = [
    { value: "upfront_payment", label: "Upfront payment" },
    { value: "equal_monthly_payments", label: "Equal monthly payments" },
    { value: "per_service_completion", label: "Per service completion" },
    { value: "per_milestone", label: "Per milestone" },
    { value: "post_delivery", label: "Post-delivery" },
  ];

  useEffect(() => {
    if (productionFormData) {
      setCrewListDropdown(
        productionFormData.crewList.map(
          (crew: { name: string; id: string }) => ({
            label: crew.name,
            value: crew.id,
          })
        )
      );
      setManagerListDropdown(
        productionFormData.accountManagerList.map(
          (manager: { first_name: string; last_name: string; id: string }) => ({
            label: `${manager.first_name} ${manager.last_name}`,
            value: manager.id,
          })
        )
      );
      setProductionListDropdown(
        productionFormData.productionManagerList.map(
          (manager: { first_name: string; last_name: string; id: string }) => ({
            label: `${manager.first_name} ${manager.last_name}`,
            value: manager.id,
          })
        )
      );
    }
  }, [productionFormData, setValue]);

  useEffect(() => {
    if (contractDetailDataById) {
      setValue("name", contractDetailDataById?.name || "");
      setValue("manager_id", contractDetailDataById?.account_manager_id || "");
    }
  }, [contractDetailDataById, setValue]);

  return (
    <div className="tabs_content">
      <Card className="service_tabs_card">
        <h4 style={{ marginBottom: "16px" }}>General</h4>
        <Row gutter={8} style={{ width: "672px" }}>
          <Col span={24} className="">
            <InputField
              fieldName="name"
              label="Contract name"
              control={control}
              defaultValue=""
              iProps={{
                placeholder: "Contract name",
              }}
              isRequired
              rules={{
                required: "Contract name is required.",
              }}
              validate={errors.name && "error"}
              validMessage={errors.name && errors.name.message}
              tooltipTitle="Contract name"
            />
          </Col>
        </Row>
      </Card>
      <Card className="service_tabs_card">
        <h4 style={{ marginBottom: "16px" }}>Assignees</h4>
        <Row gutter={8} style={{ width: "672px" }}>
          <Col span={24}>
            <SelectField
              fieldName="manager_id"
              label="Account manager "
              control={control}
              selectOption={managerListDropdown || []}
              iProps={{
                placeholder: "Select account manager",
              }}
              isRequired
              rules={{
                required: "Account manager is required.",
              }}
              validate={errors.manager_id && "error"}
              validMessage={errors.manager_id && errors.manager_id.message}
              tooltipTitle="Select account manager"
            />
          </Col>
          <Col span={24}>
            <Card className="assign_prod_items">
              <Row gutter={20}>
                <Col span={24} style={{ marginBottom: "20px" }}>
                  <h4 style={{ marginBottom: "4px" }}>
                    Assign production teams
                  </h4>
                  <p>
                    This contract has multiple service lines which may be
                    handled by different production managers and crews. Select a
                    team for each service line.
                  </p>
                </Col>

                <Col span={24}>
                  <Row gutter={16}>
                    <Col
                      span={8}
                      style={{
                        fontWeight: 500,
                        fontSize: "14px",
                        marginBottom: "4px",
                      }}
                    >
                      Service line
                    </Col>
                    <Col
                      span={8}
                      style={{
                        fontWeight: 500,
                        fontSize: "14px",
                        marginBottom: "4px",
                      }}
                    >
                      <span style={{ display: "flex" }}>
                        Production manager <div className="required_circle" />
                      </span>
                    </Col>
                    <Col
                      span={8}
                      style={{
                        fontWeight: 500,
                        fontSize: "14px",
                        marginBottom: "4px",
                      }}
                    >
                      <span style={{ display: "flex" }}>
                        Crew <div className="required_circle" />
                      </span>
                    </Col>
                  </Row>
                </Col>

                <Col span={24}>
                  <ProductionTeams
                    control={control}
                    append={append}
                    remove={remove}
                    fields={fields}
                    errors={errors}
                    crewListDropdown={crewListDropdown}
                    productionListDropdown={productionListDropdown}
                  />
                </Col>
              </Row>
            </Card>

            <Card>
              <Row gutter={20} style={{ marginBottom: "20px" }}>
                <Col span={24}>
                  <h4> Contract terms </h4>
                </Col>
              </Row>

              <Row
                gutter={20}
                style={{
                  backgroundColor: "#F6F9F9",
                  padding: "16px",
                  borderRadius: "16px",
                  marginLeft: "0px",
                  marginRight: "0px",
                  marginBottom: "20px",
                }}
              >
                <Col span={12} style={{ marginBottom: "16px" }}>
                  <Flex
                    vertical
                    gap={8}
                    style={{
                      fontSize: "14px",
                      lineHeight: "20px",
                      fontWeight: 500,
                    }}
                  >
                    Renewable
                    <div className="potentialDealsRadio cs_disabledRadio">
                      <Radio.Group
                        block
                        options={optionsRenewable}
                        value={renewOption}
                        onChange={onRenewChange}
                        optionType="button"
                        buttonStyle="solid"
                        disabled
                      />
                    </div>
                  </Flex>
                </Col>
                <Col span={12} style={{ marginBottom: "16px" }}>
                  <Flex
                    vertical
                    gap={8}
                    style={{
                      fontSize: "14px",
                      lineHeight: "20px",
                      fontWeight: 500,
                    }}
                  >
                    Pricing model
                    <div className="potentialDealsRadio cs_disabledRadio">
                      <Radio.Group
                        block
                        options={optionsPricing}
                        value={pricingOption}
                        onChange={onPricingChange}
                        optionType="button"
                        buttonStyle="solid"
                        disabled
                      />
                    </div>
                  </Flex>
                </Col>

                <Col span={12} className="mb-0">
                  <SelectField
                    fieldName="billing"
                    label="Billing"
                    control={control}
                    selectOption={billingDropdown}
                    iProps={{
                      placeholder: "Select billing type",
                      disabled: true,
                    }}
                    tooltipTitle="Billing"
                  />
                </Col>
                <Col span={12} className="mb-0">
                  <SelectField
                    fieldName="net_terms"
                    label="Net terms"
                    control={control}
                    selectOption={netTermDropdown}
                    iProps={{
                      placeholder: "Select net terms",
                      disabled: true,
                    }}
                    tooltipTitle="Net terms"
                  />
                </Col>
              </Row>

              <Row gutter={20} style={{ marginBottom: "20px" }}>
                <Col span={12} className="mb-0">
                  <DateField
                    fieldName="start_date"
                    label="Start date"
                    control={control}
                    tooltipTitle="Start date"
                    isRequired
                    rules={{
                      required: "Start date is required.",
                    }}
                    validate={errors.start_date && "error"}
                    validMessage={
                      errors.start_date && errors.start_date.message
                    }
                    iProps={{
                      disabled: true,
                    }}
                  />
                </Col>
                <Col span={12} className="mb-0">
                  <DateField
                    fieldName="end_date"
                    label="End date"
                    control={control}
                    tooltipTitle="End date"
                    isRequired
                    rules={{
                      required: "End date is required.",
                    }}
                    validate={errors.end_date && "error"}
                    validMessage={errors.end_date && errors.end_date.message}
                    iProps={{
                      disabled: true,
                    }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );
}
